import type {Dispatch, SetStateAction} from 'react';
import {createContext, useContext} from 'react';

import {ProcessSingletonTaskSchemaName} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {ProcessTaskDefinitionsResult} from '../useAllSchemasById';
import type {LoadedFlowchartData} from './executionFlowchartData';
import type {ExecutionEditorMode} from './ExecutionFlowchartPaneTypes';

/**
 * READY execution editor inputs (schemas, draft, configurator, mode). Set by
 * {@link ExecutionFlowchartEditor}; read with {@link useExecutionFlowchartRuntime}.
 */
export interface ExecutionFlowchartRuntimeValue {
  editorMode: ExecutionEditorMode;
  data: LoadedFlowchartData;
  executionEditorConfigurator: ProcessFlowchartEditorConfigurator;
  draftState: FlowchartConfigWithMetadata;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  onBlockingErrorsChange?: (hasBlockingErrors: boolean) => void;
  allSchemasById: SchemaById;
  taskDefinitionsResult: ProcessTaskDefinitionsResult;
  singletonTaskSchemasByName: Record<
    ProcessSingletonTaskSchemaName,
    ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  > | null;
}

const ExecutionFlowchartRuntimeContext = createContext<ExecutionFlowchartRuntimeValue | null>(null);

export {ExecutionFlowchartRuntimeContext};

/** Reads {@link ExecutionFlowchartRuntimeValue} from {@link ExecutionFlowchartRuntimeContext}. */
export function useExecutionFlowchartRuntime(): ExecutionFlowchartRuntimeValue {
  const value = useContext(ExecutionFlowchartRuntimeContext);
  if (value == null) {
    throw new Error(
      'useExecutionFlowchartRuntime must be used under ExecutionFlowchartRuntimeContext.Provider'
    );
  }
  return value;
}
